import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { quotationsApi, type QuotationChangeRequest, type QuotationSend } from './api';
import type {
  ConversionRateParams,
  ConversionRateResponse,
  CreateSalesOrderFromQuoteInput,
  Quotation,
  QuotationListParams,
  QuotationListResponse,
  QuotationRefundMode,
  SendQuotationInput,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useQuotations(params: QuotationListParams): UseQueryResult<QuotationListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'quotations',
      tenantId,
      params.page ?? 1,
      params.status ?? '',
      params.search ?? '',
      params.start_date ?? '',
      params.end_date ?? '',
      params.location_id ?? '',
      params.sort ?? '',
      params.customer_name ?? '',
      params.mobile ?? '',
      params.quote_number ?? '',
    ],
    queryFn: () => quotationsApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useQuotation(id: string | undefined): UseQueryResult<Quotation | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['quotations', tenantId, 'detail', id],
    queryFn: () => (id ? quotationsApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useConversionRate(
  params: ConversionRateParams,
  enabled = true,
): UseQueryResult<ConversionRateResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'quotations',
      tenantId,
      'conversion-rate',
      params.location_id ?? '',
      params.created_by ?? '',
      params.start_date ?? '',
      params.end_date ?? '',
    ],
    queryFn: () => quotationsApi.conversionRate(params),
    staleTime: 60_000,
    enabled,
  });
}

export function useCreateSalesOrderFromQuote() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSalesOrderFromQuoteInput) => quotationsApi.createSalesOrder(input),
    onSuccess: () => {
      // The quote flips to CONVERTED and a new sales order exists.
      qc.invalidateQueries({ queryKey: ['quotations', tenantId] });
      qc.invalidateQueries({ queryKey: ['sales-orders', tenantId] });
    },
  });
}

export function useSendQuotation() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SendQuotationInput }) =>
      quotationsApi.send(id, input),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['quotations', tenantId, 'sends', id] });
    },
  });
}

export function useCancelQuotation() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      refundMode,
      approvalToken,
    }: {
      id: string;
      refundMode?: QuotationRefundMode;
      approvalToken?: string;
    }) => quotationsApi.cancel(id, refundMode, approvalToken),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['quotations', tenantId] });
      qc.invalidateQueries({ queryKey: ['quotations', tenantId, 'detail', id] });
    },
  });
}

export function useQuotationSends(id: string | undefined): UseQueryResult<QuotationSend[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['quotations', tenantId, 'sends', id],
    queryFn: () => (id ? quotationsApi.listSends(id) : Promise.resolve([] as QuotationSend[])),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useQuotationChangeRequests(
  id: string | undefined,
): UseQueryResult<QuotationChangeRequest[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['quotations', tenantId, 'change-requests', id],
    queryFn: () =>
      id ? quotationsApi.listChangeRequests(id) : Promise.resolve([] as QuotationChangeRequest[]),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}
